import { FunctionComponent, useEffect, useRef } from 'react'
import { useCheckoutToken } from '../../state'
import { isCashGrantHandoffPending } from './cashAuthJourney'
import { buildJourneyStartedEvent, buildRecordCompletedJourneyPayload } from './contract'
import { EsidContextProps, useEsid } from './EsidContext'
import { clearJourneyMarker, isMarkerFromCurrentPageLoad, readJourneyMarker, writeJourneyMarker } from './journeyMarker'
import { useCheckoutJourneyOwnership } from './MobileUserJourneyOwnership'
import { datadogUserJourneySink, UserJourneySink } from './sink'
import { JourneyOutcome, JourneyTerminationReason, UserJourney } from './types'
import { useUserJourneyContext } from './UserJourneyContext'
import { USER_JOURNEY_NAMES } from './constants'
import { getReplacedJourneyCompletion } from './timeout'

export interface UserJourneyReportingProps {
  sink?: UserJourneySink
}

export const UserJourneyReporting: FunctionComponent<UserJourneyReportingProps> = ({ sink = datadogUserJourneySink }) => {
  const { claimRocketshipOwnership, isRocketshipOwner, journeyId, owner, reportingEnabled: enabled } = useCheckoutJourneyOwnership()
  const { getActiveJourney, subscribe } = useUserJourneyContext()
  const checkoutToken = useCheckoutToken()
  const { esid }: EsidContextProps = useEsid()
  const reportedCompletions = useRef<Set<string>>(new Set())
  const reconciledMarker = useRef(false)
  const resumedEsids = useRef<Map<string, string>>(new Map())
  const monotonicStarts = useRef<Map<string, number>>(new Map())
  useEffect(() => {
    if (owner === 'afterpay-mobile') {
      const marker = readJourneyMarker()
      const isForeignCashHandoff = marker?.handoff === true && marker.checkoutToken !== undefined && marker.checkoutToken !== checkoutToken
      if (
        enabled &&
        journeyId !== undefined &&
        marker !== undefined &&
        !isForeignCashHandoff &&
        (marker.journey.id !== journeyId || isMarkerFromCurrentPageLoad(marker)) &&
        !reportedCompletions.current.has(marker.journey.id)
      ) {
        reportedCompletions.current.add(marker.journey.id)
        try {
          const completion =
            marker.handoff === true
              ? getReplacedJourneyCompletion(marker.journey.created)
              : {
                  outcome: isMarkerFromCurrentPageLoad(marker) ? JourneyOutcome.PROCESS_CRASHED : JourneyOutcome.PROCESS_KILLED,
                  terminationReason: undefined,
                }
          sink.journeyCompleted(buildRecordCompletedJourneyPayload(marker.journey, completion.outcome, completion.terminationReason, marker.esid))
        } catch {
          reportedCompletions.current.delete(marker.journey.id)
          return undefined
        }
      }
      clearJourneyMarker()
      return undefined
    }

    if (!enabled) {
      if (reconciledMarker.current) {
        clearJourneyMarker()
      }
      return undefined
    }

    const reportCompleted = (journey: UserJourney, outcome: JourneyOutcome, terminationReason?: JourneyTerminationReason): void => {
      if (reportedCompletions.current.has(journey.id)) {
        return
      }
      reportedCompletions.current.add(journey.id)
      clearJourneyMarker()
      sink.journeyCompleted(
        buildRecordCompletedJourneyPayload(
          journey,
          outcome,
          terminationReason,
          resumedEsids.current.get(journey.id) ?? esid,
          monotonicStarts.current.get(journey.id)
        )
      )
      monotonicStarts.current.delete(journey.id)
    }

    if (owner === 'rocketship') {
      const activeJourney = getActiveJourney()
      if (!reconciledMarker.current) {
        reconciledMarker.current = true
        try {
          const orphaned = readJourneyMarker()
          const canResumePersistedBoundary =
            orphaned !== undefined &&
            (orphaned.journey.id === activeJourney?.id || (!isMarkerFromCurrentPageLoad(orphaned) && orphaned.journey.id === journeyId))
          const reconcilesCurrentOfferedBoundary = orphaned !== undefined && isMarkerFromCurrentPageLoad(orphaned) && orphaned.journey.id === journeyId
          let reconciledOrphan = false
          if (orphaned !== undefined && orphaned.handoff !== true && !canResumePersistedBoundary && !reportedCompletions.current.has(orphaned.journey.id)) {
            reportedCompletions.current.add(orphaned.journey.id)
            const outcome = isMarkerFromCurrentPageLoad(orphaned) ? JourneyOutcome.PROCESS_CRASHED : JourneyOutcome.PROCESS_KILLED
            try {
              sink.journeyCompleted(buildRecordCompletedJourneyPayload(orphaned.journey, outcome, undefined, orphaned.esid))
              if (reconcilesCurrentOfferedBoundary) {
                claimRocketshipOwnership()
              }
            } catch (error) {
              reportedCompletions.current.delete(orphaned.journey.id)
              throw error
            }
            clearJourneyMarker()
            reconciledOrphan = true
          }
          if (activeJourney !== undefined && (orphaned === undefined || reconciledOrphan)) {
            writeJourneyMarker(activeJourney, resumedEsids.current.get(activeJourney.id) ?? esid, {
              checkoutToken,
              handoff: isCashGrantHandoffPending(),
            })
          }
        } catch {
          reconciledMarker.current = false
        }
      } else {
        if (activeJourney !== undefined) {
          writeJourneyMarker(activeJourney, resumedEsids.current.get(activeJourney.id) ?? esid, {
            checkoutToken,
            handoff: isCashGrantHandoffPending(),
          })
        }
      }
    }

    return subscribe(({ action, prevState, state }) => {
      if (action.name === USER_JOURNEY_NAMES.CHECKOUT && !isRocketshipOwner()) return

      switch (action.type) {
        case 'USER_JOURNEY_START': {
          const prior = prevState.journeys[action.name]
          if (prior !== undefined) {
            reportCompleted(prior, JourneyOutcome.TERMINATED, JourneyTerminationReason.JOURNEY_RESTARTED)
          }
          const started = state.journeys[action.name]
          if (started !== undefined) {
            monotonicStarts.current.set(started.id, performance.now())
            writeJourneyMarker(started, esid, { checkoutToken, replaceCheckoutToken: true })
            sink.journeyStarted(buildJourneyStartedEvent(started, esid))
          }
          break
        }
        case 'USER_JOURNEY_RESUME': {
          const resumedJourney = state.journeys[action.name]
          if (resumedJourney !== undefined && prevState.journeys[action.name] === undefined) {
            resumedEsids.current.set(resumedJourney.id, action.esid)
            writeJourneyMarker(resumedJourney, action.esid, { checkoutToken })
          }
          break
        }
        case 'USER_JOURNEY_COMPLETE': {
          const journey = prevState.journeys[action.name]
          if (journey !== undefined) {
            reportCompleted(journey, action.outcome, action.terminationReason)
          }
          break
        }
        default: {
          const journey = state.journeys[action.name]
          if (journey !== undefined) {
            writeJourneyMarker(journey, resumedEsids.current.get(journey.id) ?? esid, {
              checkoutToken,
              handoff: isCashGrantHandoffPending(),
            })
          }
          break
        }
      }
    })
  }, [claimRocketshipOwnership, checkoutToken, enabled, owner, subscribe, esid, sink, isRocketshipOwner, journeyId, getActiveJourney])

  return null
}
